import { ReactComponent as CheckmarkIcon } from "assets/icons/successMessageCheckmark.svg";
import { createContext, useContext, useState, useCallback, useRef, useEffect } from "react";
import CloseButton from "reusableComponents/CloseButton";
import styles from "./styles.module.scss";

const ModalContext = createContext();

export const ModalProvider = ({ children }) => {
  const [activeModal, setActiveModal] = useState(false);
  const [modalContent, setModalContent] = useState(null);
  const [modalTitle, setModalTitle] = useState("");
  const [isClosing, setIsClosing] = useState(false);
  const [autoCloseTimer, setAutoCloseTimer] = useState(null);
  const [modalType, setModalType] = useState("");
  const [modalSize, setModalSize] = useState("medium");
  const pendingCallbackRef = useRef(null);
  const closeCallbackRef = useRef(null);
  const isMobileRef = useRef(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => {
      isMobileRef.current = window.innerWidth <= 768;
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const sizeMap = {
    small: 430,
    medium: 900,
    large: 1024,
    booking: 1024,
  };

  const openModal = ({
    content = null,
    title = "",
    type = "default",
    autoClose = false,
    closeDelay = 3000,
    size = "small",
    onClose = null,
  } = {}) => {
    pendingCallbackRef.current = null;
    closeCallbackRef.current = onClose;
    
    setModalContent(content);
    setModalTitle(title);
    setModalType(type);
    setModalSize(size);
    setActiveModal(true);
    setIsClosing(false);

    if (autoClose) {
      const timer = setTimeout(() => {
        closeModal();
      }, closeDelay);
      setAutoCloseTimer(timer);
    }
  };

  const closeModal = useCallback((navigationCallback = null) => {
    if (navigationCallback && typeof navigationCallback === 'function') {
      pendingCallbackRef.current = navigationCallback;
    }
    
    setIsClosing(true);
    
    const animationDuration = isMobileRef.current ? 500 : 300;
    
    setTimeout(() => {
      setActiveModal(false);
      setModalContent(null);
      setModalTitle("");
      setIsClosing(false);
      
      if (autoCloseTimer) {
        clearTimeout(autoCloseTimer);
        setAutoCloseTimer(null);
      }
      
      if (closeCallbackRef.current) {
        closeCallbackRef.current();
        closeCallbackRef.current = null;
      }
      
      if (pendingCallbackRef.current) {
        const callbackDelay = isMobileRef.current ? 150 : 50;
        setTimeout(() => {
          pendingCallbackRef.current();
          pendingCallbackRef.current = null;
        }, callbackDelay);
      }
    }, animationDuration);
  }, [autoCloseTimer]);

  // New method for immediate closing (for navigation scenarios)
  const closeModalImmediately = useCallback(() => {
    // Clear any pending timers
    if (autoCloseTimer) {
      clearTimeout(autoCloseTimer);
      setAutoCloseTimer(null);
    }
    
    // Close callbacks
    if (closeCallbackRef.current) {
      closeCallbackRef.current();
      closeCallbackRef.current = null;
    }
    
    if (pendingCallbackRef.current) {
      pendingCallbackRef.current();
      pendingCallbackRef.current = null;
    }
    
    // Immediately close modal without animation
    setActiveModal(false);
    setModalContent(null);
    setModalTitle("");
    setIsClosing(false);
  }, [autoCloseTimer]);

  const getModalWidth = () => {
    return sizeMap[modalSize] || sizeMap.medium;
  };

  return (
    <ModalContext.Provider value={{ openModal, closeModal, closeModalImmediately }}>
      {children}
      {activeModal && (
        <div
          className={`${styles.modalOverlay} ${activeModal ? styles.active : ""} ${
            isClosing ? styles.closing : ""
          }`}
        >
          <div
            className={`${styles.modalContent} ${activeModal ? styles.active : ""} ${
              isClosing ? styles.closing : ""
            } ${styles[modalType]} ${styles[modalSize]}`}
            style={{ width: getModalWidth() }}
          >
            {modalTitle && (
              <div className={styles.modalHeader}>
                <p className={styles.modalTitle}>{modalTitle}</p>
                <CloseButton closeCallback={() => closeModal()} />
              </div>
            )}
            <div className={styles.modalBody}>
              {modalType === "success" && (
                <span className={styles.successIcon}>
                  <CheckmarkIcon />
                </span>
              )}
              {modalContent}
            </div>
          </div>
        </div>
      )}
    </ModalContext.Provider>
  );
};

export const useModal = () => {
  const context = useContext(ModalContext);
  if (!context) {
    throw new Error("useModal must be used within a ModalProvider");
  }
  return context;
};